import * as React from "react";
import { useState, useEffect } from "react";
import {
  Button,
  Paper,
  Typography,
  TextField,
  Alert,
  Snackbar,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Divider,
  Radio,
  RadioGroup,
  IconButton,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { TabPanelHeader } from "../../components/TabPanelHeader";
import { useGameConfigService, IStaticDataFileMapping } from "../../services/backend/GameConfigService";
import { LoadingOverlay } from "../../components/LoadingOverlay";

// Local storage keys
const HUB_SPREADSHEET_URL_KEY = "townsfolk_hub_spreadsheet_url";
const CUSTOM_SPREADSHEET_URLS_KEY = "townsfolk_custom_spreadsheet_urls";
const IMPORT_TYPE_KEY = "townsfolk_import_type";

type ImportType = "hub" | "custom";

interface StaticDataItemMappingProps {
  item: IStaticDataFileMapping;
  onChange: (updatedItem: IStaticDataFileMapping) => void;
}

const StaticDataItemMapping: React.FC<StaticDataItemMappingProps> = ({ item, onChange }) => {
  const [isNewItem, setIsNewItem] = useState<boolean>(!item.currentId && !item.currentSDId);

  const handleNewItemChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setIsNewItem(newValue);

    // If marked as new item, clear current fields
    if (newValue) {
      onChange({
        ...item,
        currentId: undefined,
        currentSDId: undefined,
      });
    }
  };

  const handleCurrentIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...item,
      currentId: event.target.value,
    });
  };

  const handleCurrentSDIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...item,
      currentSDId: event.target.value,
    });
  };

  return (
    <Box sx={{ py: 2, borderBottom: "1px solid rgba(0, 0, 0, 0.12)" }}>
      <Grid container spacing={2} alignItems="center">
        <Grid size={2}>
          <Typography variant="body2" fontWeight="medium">
            {item.id}
          </Typography>
        </Grid>
        <Grid size={2}>
          <Typography variant="body2" fontWeight="medium">
            {item.database}
          </Typography>
        </Grid>
        <Grid size={2}>
          <FormControlLabel
            control={<Checkbox checked={isNewItem} onChange={handleNewItemChange} />}
            label="This is a new item"
          />
        </Grid>
        <Grid size={3}>
          <TextField
            label="Previous Id"
            size="small"
            fullWidth
            value={item.currentId || ""}
            onChange={handleCurrentIdChange}
            disabled={isNewItem}
          />
        </Grid>
        <Grid size={3}>
          <TextField
            label="Previous SDId"
            size="small"
            fullWidth
            value={item.currentSDId || ""}
            onChange={handleCurrentSDIdChange}
            disabled={isNewItem}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export interface ImportDataTabPanelProps {
  selectedBranch: string | undefined;
  initialRemainingIds?: IStaticDataFileMapping[];
  toolboxOutput: string | undefined;
  onToolboxOutputUpdate: (toolboxOutput: string | undefined) => void;
}

export const ImportDataTabPanel = ({
  selectedBranch,
  initialRemainingIds,
  toolboxOutput,
  onToolboxOutputUpdate,
}: ImportDataTabPanelProps) => {
  const gameConfigService = useGameConfigService();

  // Initialize state with values from localStorage if available
  const [importType, setImportType] = useState<ImportType>(() => {
    const savedType = localStorage.getItem(IMPORT_TYPE_KEY);
    return (savedType as ImportType) || "hub";
  });

  const [hubSpreadsheetUrl, setHubSpreadsheetUrl] = useState<string>(() => {
    const savedUrl = localStorage.getItem(HUB_SPREADSHEET_URL_KEY);
    return savedUrl || "";
  });

  const [customSpreadsheetUrls, setCustomSpreadsheetUrls] = useState<string[]>(() => {
    const savedUrls = localStorage.getItem(CUSTOM_SPREADSHEET_URLS_KEY);
    return savedUrls ? JSON.parse(savedUrls) : [""];
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string | undefined>(undefined);
  const [alertSeverity, setAlertSeverity] = useState<"success" | "error" | "info" | "warning">("info");
  const [remainingItems, setRemainingItems] = useState<IStaticDataFileMapping[]>(initialRemainingIds || []);

  // Save to localStorage whenever values change
  useEffect(() => {
    localStorage.setItem(IMPORT_TYPE_KEY, importType);
  }, [importType]);

  useEffect(() => {
    localStorage.setItem(HUB_SPREADSHEET_URL_KEY, hubSpreadsheetUrl);
  }, [hubSpreadsheetUrl]);

  useEffect(() => {
    localStorage.setItem(CUSTOM_SPREADSHEET_URLS_KEY, JSON.stringify(customSpreadsheetUrls));
  }, [customSpreadsheetUrls]);

  // Update remainingItems when initialRemainingIds changes
  useEffect(() => {
    if (initialRemainingIds) {
      setRemainingItems(initialRemainingIds);
    }
  }, [initialRemainingIds]);

  const handleItemChange = (updatedItem: IStaticDataFileMapping, index: number) => {
    const newItems = [...remainingItems];
    newItems[index] = updatedItem;
    setRemainingItems(newItems);
  };

  const handleCustomUrlChange = (index: number, value: string) => {
    const newUrls = [...customSpreadsheetUrls];
    newUrls[index] = value;
    setCustomSpreadsheetUrls(newUrls);
  };

  const addCustomUrl = () => {
    setCustomSpreadsheetUrls([...customSpreadsheetUrls, ""]);
  };

  const removeCustomUrl = (index: number) => {
    if (customSpreadsheetUrls.length > 1) {
      const newUrls = customSpreadsheetUrls.filter((_, i) => i !== index);
      setCustomSpreadsheetUrls(newUrls);
    }
  };

  const handleImport = async () => {
    if (!selectedBranch) {
      setAlertMessage("Please select a branch first");
      setAlertSeverity("error");
      return;
    }

    // Validate inputs based on import type
    if (importType === "hub") {
      if (!hubSpreadsheetUrl.trim()) {
        setAlertMessage("Please enter a valid Hub Spreadsheet URL");
        setAlertSeverity("error");
        return;
      }
    } else if (importType === "custom") {
      const validUrls = customSpreadsheetUrls.filter((url) => url.trim());
      if (validUrls.length === 0) {
        setAlertMessage("Please enter at least one valid Custom Spreadsheet URL");
        setAlertSeverity("error");
        return;
      }
    }

    try {
      setIsLoading(true);
      const mappingResult = await gameConfigService.importSpreadsheets(
        selectedBranch,
        importType === "hub" ? hubSpreadsheetUrl : undefined,
        importType === "custom" ? customSpreadsheetUrls.filter((url) => url.trim()) : undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        remainingItems.length > 0 ? remainingItems : undefined,
      );

      if (mappingResult) {
        if (mappingResult.remainingIds && mappingResult.remainingIds.length > 0) {
          // We have unmapped items that need attention
          setRemainingItems(mappingResult.remainingIds);
          setAlertMessage(
            `There are ${mappingResult.remainingIds.length} items that need additional mapping information. Please review below.`,
          );
          setAlertSeverity("info");
        } else {
          // Import completed successfully
          setRemainingItems([]);
          setAlertMessage("Data imported successfully.");
          setAlertSeverity("success");
        }
        if (mappingResult.output) {
          onToolboxOutputUpdate(mappingResult.output);
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to import data from Hub Spreadsheet.");
      setAlertSeverity("error");
    } finally {
      setIsLoading(false);
    }
  };

  const alertCloseHandler = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage(undefined);
  };

  if (!selectedBranch) {
    return (
      <Paper sx={{ p: 2, height: "70vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Typography variant="subtitle1" color="textSecondary">
          Please select a branch to import data.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <TabPanelHeader>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={handleImport}
          sx={{ marginRight: 2, height: 44 }}
          disabled={
            !selectedBranch ||
            (importType === "hub" && !hubSpreadsheetUrl.trim()) ||
            (importType === "custom" && !customSpreadsheetUrls.some((url) => url.trim()))
          }
        >
          Import
        </Button>
      </TabPanelHeader>
      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Import Data from Spreadsheets
        </Typography>

        <RadioGroup value={importType} onChange={(e) => setImportType(e.target.value as ImportType)} sx={{ mb: 2 }}>
          <FormControlLabel value="hub" control={<Radio />} label="Import from Hub Spreadsheet" />
          <FormControlLabel value="custom" control={<Radio />} label="Import from Custom Spreadsheets" />
        </RadioGroup>

        {importType === "hub" && (
          <Box>
            <TextField
              label="Hub Spreadsheet URL"
              value={hubSpreadsheetUrl}
              onChange={(e) => setHubSpreadsheetUrl(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="Enter the URL of the Hub Spreadsheet"
              variant="outlined"
            />
            <Typography variant="caption" color="textSecondary">
              Import game configuration data from a Hub Google Sheets spreadsheet.
            </Typography>
          </Box>
        )}

        {importType === "custom" && (
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Add one or more custom spreadsheet URLs to import from:
            </Typography>
            {customSpreadsheetUrls.map((url, index) => (
              <Box key={index} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <TextField
                  label={`Custom Spreadsheet URL ${index + 1}`}
                  value={url}
                  onChange={(e) => handleCustomUrlChange(index, e.target.value)}
                  fullWidth
                  margin="normal"
                  placeholder="Enter the URL of the Custom Spreadsheet"
                  variant="outlined"
                  sx={{ mr: 1 }}
                />
                {customSpreadsheetUrls.length > 1 && (
                  <IconButton onClick={() => removeCustomUrl(index)} color="error" sx={{ mt: 1 }}>
                    <RemoveIcon />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button startIcon={<AddIcon />} onClick={addCustomUrl} variant="outlined" sx={{ mt: 1 }}>
              Add Another URL
            </Button>
            <Typography variant="caption" color="textSecondary" sx={{ display: "block", mt: 1 }}>
              Import game configuration data from multiple custom Google Sheets spreadsheets.
            </Typography>
          </Box>
        )}
      </Paper>
      {remainingItems.length > 0 && (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Items requiring mapping
          </Typography>
          <Typography
            variant="body2"
            sx={{
              marginBottom: "16px",
            }}
          >
            The following items need additional information. For each item, either mark it as new or provide the previous Id
            and/or SDId.
          </Typography>

          <Box sx={{ mt: 3 }}>
            <Grid container spacing={2} sx={{ fontWeight: "bold", pb: 1 }}>
              <Grid size={2}>
                <Typography variant="subtitle2">Item ID</Typography>
              </Grid>
              <Grid size={2}>
                <Typography variant="subtitle2">Database</Typography>
              </Grid>
              <Grid size={2}>
                <Typography variant="subtitle2">Is new or renamed item?</Typography>
              </Grid>
              <Grid size={3}>
                <Typography variant="subtitle2">Previous ID</Typography>
              </Grid>
              <Grid size={3}>
                <Typography variant="subtitle2">Previous Game-Config Item ID (optional)</Typography>
              </Grid>
            </Grid>
            <Divider />

            {remainingItems.map((item, index) => (
              <StaticDataItemMapping
                key={item.id}
                item={item}
                onChange={(updatedItem) => handleItemChange(updatedItem, index)}
              />
            ))}

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Button variant="contained" color="primary" onClick={handleImport}>
                Complete Import
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
      {toolboxOutput && (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Toolbox Output
          </Typography>
          <pre style={{ whiteSpace: "pre-wrap" }}>{toolboxOutput}</pre>
        </Paper>
      )}
      <Snackbar open={alertMessage !== undefined} autoHideDuration={5000} onClose={alertCloseHandler}>
        <Alert onClose={alertCloseHandler} severity={alertSeverity} sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>
      {isLoading && <LoadingOverlay />}
    </>
  );
};
